/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import BookOpenIcon from '@atlaskit/icon-lab/core/book-open';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { useSubscribe } from '@atlaskit/rovo-triggers/main';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import {
	SkillMetadataProvider,
	useSkillMetadata,
} from '@atlassian/rovo-skills/services/skill-metadata-provider';

import {
	type ConversationAction,
	type ConversationActionTags,
	type ConvoStarterCategoriesMap,
	type HandleActionType,
	LEGACY_DYNAMIC_SKILL_IDS,
} from '../../common/types';
import { isChatAction, isNestedAction, isSkillAction } from '../../common/utils';
import { isPlaceholderAction } from '../../common/utils/create-placeholder-action';
import { isPageContextTypeSupportedForDynamicStarters } from '../../common/utils/dynamic-starters-context-eligibility';
import { getContentAri } from '../../common/utils/extract-urls';
import { createJTBDCategoryMap } from '../../common/utils/jtbd-category-map';
import { isPageContextTypeSupportedForRankedActions } from '../../common/utils/ranked-actions-context-eligibility';
import { truncatePlaceholder } from '../../common/utils/truncate-placeholder';
import { useWindowLocation } from '../../common/utils/use-window-location';
import { useActionsFetching } from '../../controllers/actions-fetching';
import { useConversationActionsStoreState } from '../../controllers/conversation-actions-store';
import { usePageContextType } from '../../controllers/rovo-conversation-starters';
import { useFetchRankedActions } from '../../services/fetch-ranked-actions';
import { NestedViewHeading } from '../rovo-conversation-actions/components/nested-view-heading';

import {
	ConversationActionCompactView,
	type DiscoverPromptLibraryButtonProps,
} from './components/compact-view';
import { ConversationActionsSkeleton } from './components/conversation-actions-skeleton';
import { ConversationActionExpandedView } from './components/expanded-view';

const CONFLUENCE_FEATURE_SURFACE = 'CONFLUENCE_PAGE_VIEW';

const styles = cssMap({
	conversationActionsContainerStyles: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
});

export const i18n: {
	actionsSectionText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	discoverPromptLibraryButtonLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	actionsSectionText: {
		id: 'rovo-conversation-actions.actions.action-section-text',
		defaultMessage: 'Actions',
		description: 'The section text for the AI actions in Rovo conversation actions',
	},
	discoverPromptLibraryButtonLabel: {
		id: 'rovo-conversation-actions.actions.discover-prompt-library-button',
		defaultMessage: 'Discover prompt library',
		description: 'Label for the discover prompt library button in the sidebar empty state',
	},
});

type RovoConversationActionsProps = {
	closeChat: () => void;
	setChatInputPlaceholderOverride?: (placeholderOverride: string) => void;
	handleClickDynamicPrompt?: (starter: ConversationAction) => void;
	sendMessage: (prompt: string, handleType: HandleActionType, agent?: Agent) => void;
	tags?: ConversationActionTags;
	isInSideBar?: boolean;
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
	setActiveAction: (action: ConversationAction | undefined) => void;
	getActions?: () => Promise<ConversationAction[]>;
	prioritizeCustomActionResults?: boolean;
	onDiscoverPromptLibraryClick?: () => void;
	/** Extra attributes merged into every action-clicked analytics event. Use to pass caller-specific context (e.g. spaceId, spaceName). */
	analyticsAttributes?: Record<string, unknown>;
	onNestedViewChange?: (isNested: boolean) => void;
};

function RovoConversationActionsInner({
	closeChat,
	setChatInputPlaceholderOverride,
	handleClickDynamicPrompt,
	sendMessage,
	isInSideBar,
	assistanceServiceParams,
	setActiveAction,
	onDiscoverPromptLibraryClick,
	analyticsAttributes,
	onNestedViewChange,
}: RovoConversationActionsProps) {
	const [showExpandedView, setShowExpandedView] = useState(false);
	const [nestedHeading, setNestedHeading] = useState('');
	const [nestedParent, setNestedParent] = useState<ConversationAction | null>(null);
	const location = useWindowLocation(true);
	const [triggerId] = useState(() => crypto.randomUUID());
	const [safeToDisplayRankedActions, setSafeToDisplayRankedActions] = useState(false);

	const pageContextType = usePageContextType();
	const pageContextSupportedForDynamicStarters =
		isPageContextTypeSupportedForDynamicStarters(pageContextType);

	// Get the current store state to use as initial value for actionsToRender
	// This prevents showing a skeleton flash when the component remounts (e.g., mode switch)
	const { buttonClickedTimestamp, actions: actionsFromStore } = useConversationActionsStoreState();

	const skillMetadata = useSkillMetadata();

	const { actions, isDynamicStarterActionsLoading } = useActionsFetching({
		pageContextType,
		assistanceServiceParams,
		location,
		pageContextSupportedForDynamicStarters,
	});

	// Initialize with store value to prevent flash of skeleton when component remounts
	const [actionsToRender, setActionsToRender] = useState<ConversationAction[]>(actionsFromStore);

	useEffect(() => {
		if (nestedParent === null) {
			setActionsToRender(actions);
		}
	}, [actions, nestedParent]);

	const pageContextSupportedForRankedActions =
		isPageContextTypeSupportedForRankedActions(pageContextType);
	const enableRankedActions = pageContextSupportedForRankedActions
		? FeatureGates.checkGate('trex-282_ranked-actions-route-to-backend-api')
		: false;
	const { rankedActions, isLoading: isRankedActionsLoading } = useFetchRankedActions(
		triggerId,
		assistanceServiceParams?.siteId ?? '',
		enableRankedActions,
		pageContextSupportedForRankedActions ? CONFLUENCE_FEATURE_SURFACE : undefined,
	);

	const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
	// Prevent analytics events from firing multiple times as actionsToRender changes (e.g., when dynamic starters load)
	const hasLoggedActionScreenAnalyticsRef = useRef(false);
	const hasLoggedActionScreenWithLoadedStartersAnalyticsRef = useRef(false);
	const hasLoggedExpandedScreenRef = useRef(false);
	const hasLoggedExpandedScreenWithLoadedStartersRef = useRef(false);
	const { sendAnalyticsEvent } = useAnalytics();

	const { formatMessage } = useIntl();
	const contentAri = useMemo(() => {
		return getContentAri(assistanceServiceParams?.siteId ?? '', location, pageContextType);
	}, [assistanceServiceParams?.siteId, location, pageContextType]);
	const pageContextAnalyticsAttributes = useMemo(
		() => ({ pageContextType: String(pageContextType) }),
		[pageContextType],
	);

	useEffect(() => {
		// if ranked actions are still loading or dynamic convo starters are still loading, do not display ranked actions
		if (isRankedActionsLoading || isDynamicStarterActionsLoading || actionsToRender.length === 0) {
			setSafeToDisplayRankedActions(false);
			return;
		}
		setSafeToDisplayRankedActions(true);
	}, [isRankedActionsLoading, isDynamicStarterActionsLoading, actionsToRender.length]);

	const shouldShowExpandCollapseButton = useMemo(() => {
		return actionsToRender.length > 5;
	}, [actionsToRender.length]);

	const hasPlaceholders = useMemo(() => {
		return actionsToRender.some(isPlaceholderAction);
	}, [actionsToRender]);

	const visibleActions = useMemo(() => {
		if (showExpandedView) {
			return actionsToRender;
		}
		// Compact view logic: show 4 if there's a "show more" button, otherwise 5
		return shouldShowExpandCollapseButton
			? actionsToRender.slice(0, 4)
			: actionsToRender.slice(0, 5);
	}, [showExpandedView, actionsToRender, shouldShowExpandCollapseButton]);

	// Reset analytics refs when pathname changes
	useEffect(() => {
		hasLoggedActionScreenAnalyticsRef.current = false;
		hasLoggedActionScreenWithLoadedStartersAnalyticsRef.current = false;
		hasLoggedExpandedScreenRef.current = false;
		hasLoggedExpandedScreenWithLoadedStartersRef.current = false;
	}, [location]);

	// Fire when base action view is shown
	useEffect(() => {
		// don't fire if already logged or no actions are available yet
		if (hasLoggedActionScreenAnalyticsRef.current || actionsToRender.length === 0) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'aiMateActions',
			attributes: {
				...analyticsAttributes,
				...pageContextAnalyticsAttributes,
				actionTypesRendered: actionsToRender.map((action) => action.handleType).join(','),
				numActions: actionsToRender.length,
				isDynamicStarterActionsLoading,
				contentAri,
			},
		});

		hasLoggedActionScreenAnalyticsRef.current = true;
	}, [
		sendAnalyticsEvent,
		actionsToRender,
		isDynamicStarterActionsLoading,
		contentAri,
		analyticsAttributes,
		pageContextAnalyticsAttributes,
	]);

	// Fire when action screen is shown, and convo starters are loaded
	useEffect(() => {
		if (
			hasLoggedActionScreenWithLoadedStartersAnalyticsRef.current ||
			actionsToRender.length === 0 ||
			isDynamicStarterActionsLoading
		) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'track',
			action: 'convoStartersLoaded',
			actionSubject: 'aiMateActions',
			attributes: {
				...analyticsAttributes,
				...pageContextAnalyticsAttributes,
				actionTypesRendered: actionsToRender.map((action) => action.handleType).join(','),
				numActions: actionsToRender.length,
				numConvoStarters: {
					total: actionsToRender.filter((action) => action.type === 'convo-starter-action').length,
					dynamic: actionsToRender.filter(
						(action) =>
							action.type === 'convo-starter-action' &&
							isChatAction(action) &&
							action.starterType === 'dynamic',
					).length,
					static: actionsToRender.filter(
						(action) =>
							action.type === 'convo-starter-action' &&
							isChatAction(action) &&
							action.starterType === 'static',
					).length,
				},
				contentAri,
			},
		});

		hasLoggedActionScreenWithLoadedStartersAnalyticsRef.current = true;
	}, [
		sendAnalyticsEvent,
		actionsToRender,
		isDynamicStarterActionsLoading,
		contentAri,
		analyticsAttributes,
		pageContextAnalyticsAttributes,
	]);

	// Fire when expanded view is shown
	useEffect(() => {
		if (!showExpandedView) {
			hasLoggedExpandedScreenRef.current = false;
			return;
		}
		// don't fire if already logged, if no actions are available yet, or if in nested view
		if (
			hasLoggedExpandedScreenRef.current ||
			actionsToRender.length === 0 ||
			nestedHeading !== ''
		) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'aiMateActionsExpanded',
			attributes: {
				...analyticsAttributes,
				...pageContextAnalyticsAttributes,
				actionTypesRendered: actionsToRender.map((action) => action.handleType).join(','),
				numActions: actionsToRender.length,
				isDynamicStarterActionsLoading,
				contentAri,
			},
		});

		hasLoggedExpandedScreenRef.current = true;
	}, [
		sendAnalyticsEvent,
		showExpandedView,
		actionsToRender,
		isDynamicStarterActionsLoading,
		nestedHeading,
		contentAri,
		analyticsAttributes,
		pageContextAnalyticsAttributes,
	]);

	// Fire when expanded view is shown, and convo starters are loaded
	useEffect(() => {
		if (!showExpandedView) {
			hasLoggedExpandedScreenWithLoadedStartersRef.current = false;
			return;
		}
		// don't fire if already logged, if no actions are available yet, if in nested view, or if convo starters are still loading
		if (
			hasLoggedExpandedScreenWithLoadedStartersRef.current ||
			actionsToRender.length === 0 ||
			nestedHeading !== '' ||
			isDynamicStarterActionsLoading
		) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'track',
			action: 'convoStartersLoaded',
			actionSubject: 'aiMateActionsExpanded',
			attributes: {
				...analyticsAttributes,
				...pageContextAnalyticsAttributes,
				actionTypesRendered: actionsToRender.map((action) => action.handleType).join(','),
				numActions: actionsToRender.length,
				numConvoStarters: {
					total: actionsToRender.filter((action) => action.type === 'convo-starter-action').length,
					dynamic: actionsToRender.filter(
						(action) =>
							action.type === 'convo-starter-action' &&
							isChatAction(action) &&
							action.starterType === 'dynamic',
					).length,
					static: actionsToRender.filter(
						(action) =>
							action.type === 'convo-starter-action' &&
							isChatAction(action) &&
							action.starterType === 'static',
					).length,
				},
				contentAri,
			},
		});

		hasLoggedExpandedScreenWithLoadedStartersRef.current = true;
	}, [
		sendAnalyticsEvent,
		showExpandedView,
		actionsToRender,
		isDynamicStarterActionsLoading,
		nestedHeading,
		contentAri,
		analyticsAttributes,
		pageContextAnalyticsAttributes,
	]);

	//if we are in nested view (indicated by a non-empty nested heading), fire an analytics event
	useEffect(() => {
		if (nestedHeading !== '') {
			sendAnalyticsEvent({
				eventType: 'screen',
				name: 'aiMateActionsNested',
				attributes: {
					...pageContextAnalyticsAttributes,
					actionTypesRendered: actionsToRender.map((action) => action.handleType).join(','),
					numActions: actionsToRender.length,
					contentAri,
				},
			});
		}
	}, [
		nestedHeading,
		actionsToRender,
		sendAnalyticsEvent,
		contentAri,
		pageContextAnalyticsAttributes,
	]);

	useEffect(() => {
		// Don't fire analytics while dynamic starters are still loading
		// to avoid capturing intermediate state with static starters
		if (pageContextSupportedForDynamicStarters && isDynamicStarterActionsLoading) {
			return;
		}

		const convoStarterVisibleActions = visibleActions.filter(
			(action) => action.type === 'convo-starter-action',
		);

		const staticConvoStarterVisibleActions = convoStarterVisibleActions.filter(
			(action) => isChatAction(action) && action.starterType === 'static',
		);
		const dynamicConvoStarterVisibleActions = convoStarterVisibleActions.filter(
			(action) => isChatAction(action) && action.starterType === 'dynamic',
		);

		const convoStartersJtbd: ConvoStarterCategoriesMap = createJTBDCategoryMap(
			dynamicConvoStarterVisibleActions,
		);

		sendAnalyticsEvent({
			eventType: 'track',
			action: 'displayed',
			actionSubject: 'aiMateActions',
			attributes: {
				...pageContextAnalyticsAttributes,
				numVisibleActions: visibleActions.length,
				areConvoStartersVisible: convoStarterVisibleActions.length > 0,
				areStaticConvoStartersVisible: staticConvoStarterVisibleActions.length > 0,
				areDynamicConvoStartersVisible: dynamicConvoStarterVisibleActions.length > 0,
				numVisibleConvoStarters: convoStarterVisibleActions.length,
				numVisibleStaticConvoStarters: staticConvoStarterVisibleActions.length,
				numVisibleDynamicConvoStarters: dynamicConvoStarterVisibleActions.length,
				isPageContextTypeSupportedForDynamicStarters: pageContextSupportedForDynamicStarters,
				convoStartersJtbd,
				hasPlaceholders,
				contentAri,
				timeToDisplayActions: buttonClickedTimestamp
					? Date.now() - buttonClickedTimestamp
					: undefined,
			},
		});
	}, [
		visibleActions,
		pageContextSupportedForDynamicStarters,
		hasPlaceholders,
		isDynamicStarterActionsLoading,
		sendAnalyticsEvent,
		contentAri,
		buttonClickedTimestamp,
		pageContextAnalyticsAttributes,
	]);

	const handleShowMore = useCallback(() => {
		setShowExpandedView(true);
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'emptyStateSuggestionsExpand',
			attributes: {
				...pageContextAnalyticsAttributes,
				numActions: actionsToRender.length,
				isPageContextTypeSupportedForDynamicStarters: pageContextSupportedForDynamicStarters,
				hasPlaceholders,
				contentAri,
			},
		});
	}, [
		sendAnalyticsEvent,
		actionsToRender.length,
		pageContextSupportedForDynamicStarters,
		hasPlaceholders,
		contentAri,
		pageContextAnalyticsAttributes,
	]);

	const handleShowMoreHover = useCallback(() => {
		hoverTimerRef.current = setTimeout(() => {
			sendAnalyticsEvent({
				action: 'hovered',
				actionSubject: 'button',
				actionSubjectId: 'emptyStateSuggestionsExpand',
				attributes: {
					...pageContextAnalyticsAttributes,
					numActions: actionsToRender.length,
					isPageContextTypeSupportedForDynamicStarters: pageContextSupportedForDynamicStarters,
					hasPlaceholders,
					contentAri,
				},
			});
		}, 1000);
	}, [
		sendAnalyticsEvent,
		actionsToRender.length,
		pageContextSupportedForDynamicStarters,
		hasPlaceholders,
		contentAri,
		pageContextAnalyticsAttributes,
	]);

	const handleShowMoreLeave = useCallback(() => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
	}, [hoverTimerRef]);

	const handleShowLess = useCallback(() => {
		setShowExpandedView(false);
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'emptyStateSuggestionsCollapse',
			attributes: {
				...pageContextAnalyticsAttributes,
				numActions: actionsToRender.length,
				contentAri,
			},
		});
	}, [sendAnalyticsEvent, actionsToRender.length, contentAri, pageContextAnalyticsAttributes]);

	const handleShowLessHover = useCallback(() => {
		hoverTimerRef.current = setTimeout(() => {
			sendAnalyticsEvent({
				action: 'hovered',
				actionSubject: 'button',
				actionSubjectId: 'emptyStateSuggestionsCollapse',
				attributes: {
					...pageContextAnalyticsAttributes,
					numActions: actionsToRender.length,
					contentAri,
				},
			});
		}, 1000);
	}, [sendAnalyticsEvent, actionsToRender.length, contentAri, pageContextAnalyticsAttributes]);

	const handleShowLessLeave = useCallback(() => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
	}, [hoverTimerRef]);

	const executeAction = useCallback(
		(action: ConversationAction) => {
			//handle nested action
			if (isNestedAction(action)) {
				setActionsToRender(action.children as ConversationAction[]);
				//go to expanded view if not already there
				if (!showExpandedView) {
					setShowExpandedView(true);
				}
				setNestedHeading(action.actionHeading ?? action.title);
				setNestedParent(action);
				onNestedViewChange?.(true);
				return;
			}

			//handle chat action
			if (isChatAction(action) || isSkillAction(action)) {
				//if dynamic, handle with handler (only allowed for ChatActions or legacy adminhub skill actions)
				if (
					'dynamicPrompt' in action &&
					action.dynamicPrompt &&
					(isChatAction(action) || LEGACY_DYNAMIC_SKILL_IDS.has(action.id))
				) {
					if (fg('rovo_static_prompt_onclick_enabled')) {
						try {
							handleClickDynamicPrompt?.(action);
						} finally {
							action.onClick?.();
						}
					} else {
						handleClickDynamicPrompt?.(action);
					}
					return;
				}
				//if static, send conversation starter to chat
				if (action.staticPrompt) {
					const agent = 'agent' in action ? action.agent : undefined;
					if (fg('rovo_static_prompt_onclick_enabled')) {
						try {
							sendMessage(action.staticPrompt, action.handleType, agent);
						} finally {
							action.onClick?.();
						}
					} else {
						sendMessage(action.staticPrompt, action.handleType, agent);
					}
					return;
				}
			}

			//handle basic action
			if ('children' in action && action.children && action.children.length > 0) {
				setActionsToRender(action.children);
			}
			if (action.onClick) {
				action.onClick();
			}
			if ('closePanelOnSelection' in action && action.closePanelOnSelection) {
				closeChat();
				setActiveAction(undefined);
				return;
			}
			if ('viewConfig' in action && action.viewConfig) {
				//set active action in state store
				setActiveAction(action);
				return;
			}
		},
		[
			sendMessage,
			setActiveAction,
			showExpandedView,
			closeChat,
			handleClickDynamicPrompt,
			onNestedViewChange,
		],
	);

	// Subscribe to select-action event to execute an action programmatically
	useSubscribe({ topic: 'ai-mate-actions' }, (payload) => {
		if (!FeatureGates.checkGate('rovo_chat_select_action_subscriber')) {
			return;
		}
		if (payload.type === 'select-action' && actions.length > 0) {
			const action = actions.find((a) => a.id === payload.data.actionId);
			if (action) {
				executeAction(action);
			}
		}
	});

	const handleActionClick = useCallback(
		(action: ConversationAction, index?: number) => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'emptyStateSuggestions' as InteractionSource,
				attributes: {
					...analyticsAttributes,
					...pageContextAnalyticsAttributes,
					actionId: action.id,
					actionType: action.type,
					actionHandleType: action.handleType,
					isNested: action.isChild ?? false,
					rendered:
						action.type === 'ai-action'
							? action.closePanelOnSelection
								? 'in-product'
								: 'in-panel'
							: undefined,
					starterType: isChatAction(action) ? action.starterType : undefined,
					jtbdCategory: isChatAction(action) ? action.jtbdCategory : undefined,
					isPageContextTypeSupportedForDynamicStarters: pageContextSupportedForDynamicStarters,
					visibleActionIds: visibleActions.map((a) => a.id).join(','),
					visibleActionTypes: visibleActions.map((a) => a.type).join(','),
					visibleActionHandleTypes: visibleActions.map((a) => a.handleType).join(','),
					hasPlaceholders,
					contentAri,
					displayOrder: index,
				},
			});

			executeAction(action);
		},
		[
			executeAction,
			sendAnalyticsEvent,
			pageContextSupportedForDynamicStarters,
			hasPlaceholders,
			contentAri,
			visibleActions,
			analyticsAttributes,
			pageContextAnalyticsAttributes,
		],
	);

	const handleActionHover = useCallback(
		(action: ConversationAction, index?: number) => {
			hoverTimerRef.current = setTimeout(() => {
				sendAnalyticsEvent({
					action: 'hovered',
					actionSubject: 'button',
					actionSubjectId: 'emptyStateSuggestions',
					attributes: {
						...pageContextAnalyticsAttributes,
						actionId: action.id,
						actionType: action.type,
						actionHandleType: action.handleType,
						starterType: isChatAction(action) ? action.starterType : undefined,
						jtbdCategory: isChatAction(action) ? action.jtbdCategory : undefined,
						isNested: action.isChild ?? false,
						contentAri,
						displayOrder: index,
					},
				});
			}, 1000);

			if ((!isChatAction(action) && !isSkillAction(action)) || !setChatInputPlaceholderOverride) {
				return;
			}

			if (isSkillAction(action) && action.staticPrompt) {
				setChatInputPlaceholderOverride(truncatePlaceholder(action.staticPrompt));
				return;
			}

			const placeholderText =
				'dynamicPrompt' in action ? action.dynamicPrompt : action.staticPrompt;

			if (placeholderText) {
				setChatInputPlaceholderOverride(truncatePlaceholder(placeholderText));
			}
		},
		[
			sendAnalyticsEvent,
			setChatInputPlaceholderOverride,
			contentAri,
			pageContextAnalyticsAttributes,
		],
	);

	const handleActionLeave = useCallback(() => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}

		setChatInputPlaceholderOverride?.('');
	}, [hoverTimerRef, setChatInputPlaceholderOverride]);

	const handleBack = useCallback(() => {
		setShowExpandedView(false);
		setNestedHeading('');
		setActionsToRender(actions);
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'emptyStateSuggestionsNestedBackButton',
			attributes: {
				...pageContextAnalyticsAttributes,
				parent: nestedParent?.id,
				numActions: actionsToRender.length,
				contentAri,
			},
		});
		setNestedParent(null);
		onNestedViewChange?.(false);
	}, [
		setActionsToRender,
		actions,
		sendAnalyticsEvent,
		actionsToRender.length,
		nestedParent,
		contentAri,
		onNestedViewChange,
		pageContextAnalyticsAttributes,
	]);

	const handleBackHover = useCallback(() => {
		hoverTimerRef.current = setTimeout(() => {
			sendAnalyticsEvent({
				action: 'hovered',
				actionSubject: 'button',
				actionSubjectId: 'emptyStateSuggestionsNestedBackButton',
				attributes: {
					...pageContextAnalyticsAttributes,
					parent: nestedParent?.id,
					numActions: actionsToRender.length,
					contentAri,
				},
			});
		}, 1000);
	}, [
		sendAnalyticsEvent,
		nestedParent,
		actionsToRender.length,
		contentAri,
		pageContextAnalyticsAttributes,
	]);

	const handleBackLeave = useCallback(() => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
	}, [hoverTimerRef]);

	const handleDiscoverPromptLibraryClick = useCallback(() => {
		onDiscoverPromptLibraryClick?.();
	}, [onDiscoverPromptLibraryClick]);

	const isRenderStabilizationEnabled =
		FeatureGates.getExperimentValue<boolean>(
			'rovo_ttai_segment_render_stabilization',
			'isEnabled',
			false,
		) === true;
	const canRenderDiscoverPromptLibraryButton =
		isInSideBar &&
		Boolean(onDiscoverPromptLibraryClick) &&
		FeatureGates.getExperimentValue('rovo_chat_prompt_library_v2_exp', 'isEnabled', false);
	const renderDiscoverPromptLibraryIcon = useCallback(() => <BookOpenIcon label="" />, []);

	const stabilizedDiscoverPromptLibraryButton: DiscoverPromptLibraryButtonProps | undefined =
		useMemo(() => {
			if (!isRenderStabilizationEnabled || !canRenderDiscoverPromptLibraryButton) {
				return undefined;
			}

			return {
				title: formatMessage(i18n.discoverPromptLibraryButtonLabel),
				icon: renderDiscoverPromptLibraryIcon,
				onClick: handleDiscoverPromptLibraryClick,
			};
		}, [
			isRenderStabilizationEnabled,
			canRenderDiscoverPromptLibraryButton,
			formatMessage,
			renderDiscoverPromptLibraryIcon,
			handleDiscoverPromptLibraryClick,
		]);
	const legacyDiscoverPromptLibraryButton: DiscoverPromptLibraryButtonProps | undefined =
		!isRenderStabilizationEnabled && canRenderDiscoverPromptLibraryButton
			? {
					title: formatMessage(i18n.discoverPromptLibraryButtonLabel),
					icon: () => <BookOpenIcon label="" />,
					onClick: handleDiscoverPromptLibraryClick,
				}
			: undefined;
	const discoverPromptLibraryButton = isRenderStabilizationEnabled
		? stabilizedDiscoverPromptLibraryButton
		: legacyDiscoverPromptLibraryButton;

	return (
		<Box testId="rovo-conversation-actions" xcss={styles.conversationActionsContainerStyles}>
			{(enableRankedActions && !safeToDisplayRankedActions) ||
			(!enableRankedActions && actionsToRender.length === 0) ? (
				<ConversationActionsSkeleton actionSize={3} />
			) : (
				<>
					{nestedHeading !== '' && (
						<NestedViewHeading
							heading={nestedHeading}
							handleBack={handleBack}
							handleBackHover={handleBackHover}
							handleBackLeave={handleBackLeave}
						/>
					)}
					{showExpandedView ? (
						<ConversationActionExpandedView
							isInSideBar={isInSideBar}
							actions={visibleActions}
							actionRankingOrder={enableRankedActions ? rankedActions : undefined}
							showLessButton={shouldShowExpandCollapseButton}
							handleShowLess={handleShowLess}
							handleShowLessHover={handleShowLessHover}
							handleShowLessLeave={handleShowLessLeave}
							handleAction={handleActionClick}
							handleActionHover={handleActionHover}
							handleActionLeave={handleActionLeave}
							skillMetadataMap={skillMetadata?.skillMetadataMap}
							discoverPromptLibraryButton={discoverPromptLibraryButton}
						/>
					) : (
						<ConversationActionCompactView
							actions={enableRankedActions ? actionsToRender : visibleActions}
							showMoreButton={shouldShowExpandCollapseButton}
							handleShowMore={handleShowMore}
							handleShowMoreHover={handleShowMoreHover}
							handleShowMoreLeave={handleShowMoreLeave}
							handleAction={handleActionClick}
							actionRankingOrder={enableRankedActions ? rankedActions : undefined}
							handleActionHover={handleActionHover}
							handleActionLeave={handleActionLeave}
							skillMetadataMap={skillMetadata?.skillMetadataMap}
							discoverPromptLibraryButton={discoverPromptLibraryButton}
						/>
					)}
				</>
			)}
		</Box>
	);
}

export default function RovoConversationActions(props: RovoConversationActionsProps): JSX.Element {
	return (
		<SkillMetadataProvider siteId={props.assistanceServiceParams?.siteId ?? ''}>
			<RovoConversationActionsInner {...props} />
		</SkillMetadataProvider>
	);
}
